// index.tsx
import React, { useRef, useEffect, useState } from 'react';
import {
    Modal, View, TouchableOpacity, Animated, StyleSheet,
    Easing, Dimensions,
} from 'react-native';
import { styles } from './DbDrawer.styles';
import { DbDrawerProps } from './DbDrawer.types';

const { width: screenWidth } = Dimensions.get('window');

export const DbDrawer: React.FC<DbDrawerProps> = ({
    visible: propsVisible,
    children,
    placement = 'left',
    width = 280,
    overlayStyle,
    drawerStyle,
    onClose,
    animationDuration = 300, // 动画时长统一，确保同步
}) => {
    // 1. 内部Modal显示状态（延迟隐藏，保证动画完成）
    const [modalVisible, setModalVisible] = useState(false);

    // 2. 动画值：抽屉位移 + 遮罩透明度（核心优化）
    const translateX = useRef(new Animated.Value(
        placement === 'left' ? -width : screenWidth // 初始在屏幕外
    )).current;
    const overlayOpacity = useRef(new Animated.Value(0)).current; // 遮罩透明度动画

    // 3. 同步动画逻辑（抽屉+遮罩同步动）
    useEffect(() => {
        if (propsVisible) {
            // 打开：先显示Modal → 抽屉滑入 + 遮罩淡入（同步）
            setModalVisible(true);
            Animated.parallel([ // 并行执行多个动画
                Animated.timing(translateX, {
                    toValue: placement === 'left' ? 0 : screenWidth - width,
                    duration: animationDuration,
                    easing: Easing.out(Easing.ease), // 缓出动画，更丝滑
                    useNativeDriver: true,
                }),
                Animated.timing(overlayOpacity, {
                    toValue: 1, // 遮罩从透明→不透明
                    duration: animationDuration,
                    easing: Easing.out(Easing.ease),
                    useNativeDriver: true,
                }),
            ]).start();
        } else {
            // 关闭：抽屉滑出 + 遮罩淡出（同步）→ 动画完成后隐藏Modal
            Animated.parallel([
                Animated.timing(translateX, {
                    toValue: placement === 'left' ? -width : screenWidth,
                    duration: animationDuration,
                    easing: Easing.in(Easing.ease), // 缓入动画，关闭更自然
                    useNativeDriver: true,
                }),
                Animated.timing(overlayOpacity, {
                    toValue: 0, // 遮罩从不透明→透明
                    duration: animationDuration,
                    easing: Easing.in(Easing.ease),
                    useNativeDriver: true,
                }),
            ]).start(({ finished }) => {
                if (finished) { setModalVisible(false); } // 动画完成再隐藏
            });
        }
    }, [propsVisible, placement, width, animationDuration]);

    // 点击遮罩关闭
    const handleOverlayPress = () => {
        onClose();
    };

    return (
        <Modal
            visible={modalVisible}
            transparent
            animationType="none"
            onRequestClose={onClose}
            hardwareAccelerated={true} // 开启硬件加速，动画更流畅
        >
            {/* 遮罩层：绑定透明度动画 + 自定义样式 */}
            <Animated.View // 改用Animated.View承载遮罩动画（替代TouchableOpacity直接绑）
                style={[
                    styles.overlay,
                    overlayStyle,
                    { opacity: overlayOpacity }, // 核心：遮罩透明度动画
                ]}
            >
                {/* 点击遮罩的区域（单独拆分，避免动画影响点击） */}
                <TouchableOpacity
                    style={StyleSheet.absoluteFill} // 占满遮罩层
                    activeOpacity={1}
                    onPress={handleOverlayPress}
                />

                {/* 抽屉容器：绑定位移动画 */}
                <Animated.View
                    style={[
                        styles.drawer,
                        placement === 'right' ? styles.drawerRight : {},
                        { width },
                        { transform: [{ translateX }] },
                        drawerStyle,
                    ]}
                >
                    {children}
                </Animated.View>
            </Animated.View>
        </Modal>
    );
};
export default DbDrawer;
